/** A reference field: the linked pages as chips, "Remove" on each, and "Add" through the picker. */
import type { DocTitleV1, FieldDefV1, LinkTypeV1, LinkV1 } from '@codex/contracts';
import { Button, TextField, TypeChip } from '@codex/ui';
import { useQuery } from '@tanstack/react-query';
import { type KeyboardEvent, useState } from 'react';
import { useNotify } from '../../../shell/notices.js';
import { type OpenEvent, useOpenDoc } from '../../../shell/open-doc.js';
import { describeRefusal } from '../../model/refusals.js';
import { isLook } from '../../model/types-view.js';
import { otherEnd, refsOf, refWrite } from '../../model/values.js';
import { usePillServices } from '../services.js';

export const LINK_WARNING = 'Linked. This looks like a link the page already has.';

/** The chip's open mode as the modifier keys the open seam reads. */
function openEvent(mode: 'peek' | 'page' | 'center' | 'split'): OpenEvent {
  return {
    button: 0,
    metaKey: mode === 'page',
    ctrlKey: false,
    shiftKey: mode === 'split',
    altKey: mode === 'center',
    preventDefault() {},
  };
}

export function ReferenceEditor({
  field,
  docId,
  links,
  linkTypes,
  readOnly,
  reload,
  onOpened,
  query,
  setQuery,
}: {
  field: FieldDefV1;
  docId: string;
  links: readonly LinkV1[];
  linkTypes: readonly LinkTypeV1[];
  readOnly: boolean;
  reload: () => void;
  /** A chip opened a page: the dialog gets out of its way. */
  onOpened: () => void;
  /** The search text, kept by the dialog so its Esc can clear it. */
  query: string;
  setQuery: (text: string) => void;
}) {
  const services = usePillServices();
  const open = useOpenDoc();
  const notify = useNotify();
  const refs = refsOf(field, docId, links, linkTypes);
  const ids = refs.map((link) => otherEnd(link, docId));
  const titles = useQuery({
    queryKey: ['doc-titles', ...ids],
    queryFn: () => services.titles(ids),
    enabled: ids.length > 0,
    retry: false,
  });
  const titleOf = new Map<string, DocTitleV1>((titles.data ?? []).map((t) => [t.id, t]));
  const [adding, setAdding] = useState(false);
  const [hits, setHits] = useState<{ id: string; title: string }[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const single = field.kind === 'doc_ref';
  const canAdd = !readOnly && (!single || refs.length === 0);

  const search = async () => {
    setProblem(null);
    try {
      const found = await services.searchPicker(query);
      setHits(
        found.canon
          .filter((hit) => hit.id !== docId && !ids.includes(hit.id))
          .map((hit) => ({ id: hit.id, title: hit.title })),
      );
    } catch (error) {
      setProblem(describeRefusal(error));
    }
  };

  const link = async (otherId: string) => {
    const write = refWrite(field, docId, otherId, linkTypes);
    if (!write) return;
    setProblem(null);
    try {
      const made = await services.createLink(write.sourceId, write.body);
      if (made.warnings.length > 0) notify(LINK_WARNING);
      setAdding(false);
      setQuery('');
      setHits(null);
      reload();
    } catch (error) {
      setProblem(describeRefusal(error));
    }
  };

  const remove = async (target: LinkV1) => {
    setProblem(null);
    try {
      await services.removeLink(target.sourceId, { id: target.id, rv: target.rv });
      reload();
    } catch (error) {
      setProblem(describeRefusal(error));
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void search();
    }
  };

  return (
    <fieldset className="cx-pills__options cx-pills__field">
      <legend>{field.label}</legend>
      {refs.length === 0 ? (
        <span className="cx-pills__hint">Nothing linked.</span>
      ) : (
        <ul className="cx-pills__refs">
          {refs.map((target) => {
            const id = otherEnd(target, docId);
            const found = titleOf.get(id);
            const title = found?.title ?? 'A page';
            const typeKey = found?.typeKey ?? 'note';
            return (
              <li key={target.id} className="cx-pills__ref">
                <TypeChip
                  type={isLook(typeKey) ? typeKey : 'note'}
                  title={title}
                  preview={false}
                  size="sm"
                  onOpen={(mode) => {
                    open(id, openEvent(mode));
                    onOpened();
                  }}
                />
                {!readOnly && (
                  <Button size="sm" variant="ghost" onClick={() => void remove(target)}>
                    Remove
                    <span className="cx-visually-hidden"> {title}</span>
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canAdd &&
        (adding ? (
          <div className="cx-pills__search">
            <TextField
              label={`Find a page for ${field.label}`}
              hideLabel
              placeholder="Search pages"
              value={query}
              data-search={field.id}
              onChange={(event) => setQuery(event.currentTarget.value)}
              onKeyDown={onKeyDown}
            />
            <Button size="sm" onClick={() => void search()}>
              Search
            </Button>
            {hits !== null &&
              (hits.length === 0 ? (
                <span className="cx-pills__hint">No pages found.</span>
              ) : (
                <ul className="cx-pills__refs" aria-label="Results">
                  {hits.map((hit) => (
                    <li key={hit.id}>
                      <Button size="sm" onClick={() => void link(hit.id)}>
                        {hit.title}
                      </Button>
                    </li>
                  ))}
                </ul>
              ))}
          </div>
        ) : (
          <div>
            <Button size="sm" icon="plus" onClick={() => setAdding(true)}>
              Add
              <span className="cx-visually-hidden"> to {field.label}</span>
            </Button>
          </div>
        ))}
      {problem !== null && <span className="cx-pills__error">{problem}</span>}
    </fieldset>
  );
}
